import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, OnInit } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, map, switchMap, take, startWith } from 'rxjs';
import { RestrictionDetailsComponent } from 'src/app/components/restriction-details/restriction-details.component';
import { Restriction } from 'src/app/models/restriction';
import { RestrictionsService } from 'src/app/services/restrictions.service';
import { RestrictionDialogComponent } from 'src/app/components/restriction-dialog/restriction-dialog.component';
import { ButtonModule } from 'primeng/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ListboxModule } from 'primeng/listbox';
import { CardModule } from 'primeng/card';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { HeaderComponent } from 'src/app/components/header/header.component';
import { UserService } from 'src/app/services/user.service';
import { MOBILE_BREAKPOINT } from 'src/app/shared/breakpoints';

@Component({
  selector: 'app-fpc',
  standalone: true,
  imports: [
    RestrictionDetailsComponent,
    AsyncPipe,
    RestrictionDialogComponent,
    ButtonModule,
    FormsModule,
    ListboxModule,
    CardModule,
    ReactiveFormsModule,
    HeaderComponent
  ],
  templateUrl: './fpc.component.html',
  styleUrl: './fpc.component.scss',
})
export class FpcComponent implements OnInit {
  private currentFpcId$ = this.route.paramMap.pipe(
    map(params => params.get('fpcid') ?? '')
  );

  public fpc$ = this.currentFpcId$.pipe(
    switchMap(fpcId => this.restrictionsService.getFpcWithRestrictions$(fpcId))
  );

  public restrictions$ = this.fpc$.pipe(
    map(fpc => fpc?.restrictions ?? [])
  );

  public fpcLabel$ = this.fpc$.pipe(
    map(fpc => fpc ? `${fpc.fpcId.replace(/^FPC/i, '')} - ${fpc.name}` : '')
  );

  // Set synchronously in onEditSelected(), alongside showEditDialog, so the dialog never opens
  // with a stale/empty editRestriction (which previously raced with the restrictions$ | async pipe).
  editingRestriction: Restriction | null = null;

  selectedIndex = 0;
  selectedRestriction?: Restriction;
  showEditDialog = false;
  isAdmin$ = this.userService.isAdmin$;

  public searchTermControl = new FormControl<string>('');

  public filteredRestrictions$ = combineLatest({
    restrictions: this.restrictions$,
    searchTerm: this.searchTermControl.valueChanges.pipe(startWith('')),
  }).pipe(
    map(source => {
      let restrictions = source.restrictions ?? [];
      if (restrictions.length && source.searchTerm) {
        const normalize = (str: string) => str.toLowerCase().replace(/\s+/g, '');
        const searchNormalized = normalize(source.searchTerm.toLowerCase());
        restrictions = restrictions.filter(r =>
          normalize(r.displayName).includes(searchNormalized) ||
          normalize(r.restrictionName).includes(searchNormalized)
        );
      }
      return restrictions;
    })
  );

  showDetailsMobile = false;
  selectedRestrictionMobile: Restriction | null = null;

  isMobile$ = this.breakpointObserver.observe(MOBILE_BREAKPOINT).pipe(map(r => r.matches));

  constructor(
    private route: ActivatedRoute,
    private restrictionsService: RestrictionsService,
    private destroyRef: DestroyRef,
    private router: Router,
    private userService: UserService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onEditSelected() {
    this.restrictions$.pipe(take(1)).subscribe(restrictions => {
      const restriction = restrictions[this.selectedIndex];
      this.editingRestriction = restriction ? { ...restriction } : null;
      this.showEditDialog = true;
    });
  }

  onDialogSave(updated: Restriction) {
    this.showEditDialog = false;
  }

  onSelectMobile(restriction: Restriction, list: Restriction[]) {
    this.selectedRestrictionMobile = restriction;
    this.selectedIndex = list.indexOf(restriction);
    this.showDetailsMobile = true;
  }

  onCloseDetails() {
    this.showDetailsMobile = false;
  }

  onBack() {
    if (this.showDetailsMobile) {
      this.showDetailsMobile = false;
    } else {
      this.router.navigate(['/fpcs']);
    }
  }
}
